/**
 * Helpers de mutação otimista de lead compartilhados entre views/modais.
 *
 * Estes orquestradores não pertencem a uma view específica — são chamados
 * por modais e tabs que rodam em qualquer parte do CRM:
 *
 *   - `atualizarLeadOtimista`     — espelha edição (descrição, valor, tags, etc.)
 *                                    na lista plana + árvore funil-etapas
 *   - `adicionarLeadAoKanban`     — insere lead recém-criado na etapa
 *   - `inserirNovoLeadOtimista`   — fluxo completo de criação (merge + dispatch)
 *   - `atualizarClienteNosLeads`  — propaga edição de cliente nos leads
 *                                    denormalizados (lista plana + funil tree)
 *   - `aplicarStatusLeadNoKanban` — ganho/perda tiram o card da etapa do
 *                                    pipeline (funil.leadsFechados); voltar
 *                                    a "em_progresso" devolve à mesma etapa
 *   - `moverLeadDeEtapaNoKanban`  — move o card de fromEtapa pra toEtapa na
 *                                    árvore funil-etapas (mudança de etapa
 *                                    disparada de fora do kanban, ex.:
 *                                    checkbox "Mover lead" dentro do modal)
 *
 * Para propagar a edição do cliente em OUTROS slices (ex.: kanban de
 * oportunidades), use os helpers de `controller/shared/<dominio>/...`
 * — cada slice tem seu próprio orquestrador.
 *
 * Helpers específicos de uma view (ex.: drag-drop do kanban) ficam no
 * controller da view (`controller/lead-kanban`, etc.).
 */

import store from '@state/store'

const resolveOrigemLabel = (idOrigem, fallback) => {
  if (fallback) return fallback
  if (!idOrigem) return null
  const origens =
    (store.state.crm &&
      store.state.crm.origens &&
      store.state.crm.origens.data) ||
    []
  const match = origens.find((o) => o && o.id === idOrigem)
  return match ? match.nome : null
}

export const adicionarLeadAoKanban = ({ lead, idFunil, idEtapa } = {}) => {
  if (!lead || !idFunil || !idEtapa) return
  store.dispatch('crm/addLeadToKanban', { lead, idFunil, idEtapa })
}

// Ganho/perda tiram o card da etapa do pipeline (kanban de funil), pra
// refletir na hora o que o endpoint do kanban já faz sozinho depois de um
// F5 (não devolve leads ganhos/perdidos). Voltar a "em_progresso" devolve
// o card à mesma etapa de origem. `lead` é o objeto completo do modal —
// usado como fallback quando o card não está no bucket em memória (ex.:
// página recarregada e o lead reaberto por outra tela).
export const aplicarStatusLeadNoKanban = ({ lead, status } = {}) => {
  const idLead = lead && (lead.id_lead || lead.id)
  if (!idLead || !status) return
  store.dispatch('crm/setLeadStatusInKanban', { idLead, status, lead })
}

// Mesma mutation (MOVE_LEAD_IN_KANBAN) que lead-kanban.js usa pro drag-drop
// — reusada aqui pra qualquer trigger de mudança de etapa que não passe
// pelo próprio kanban (ex.: checkbox "Mover lead para a etapa" dentro do
// modal do lead, task-steps-lead.vue). Sem isso, a etapa muda no backend e
// na UI do modal, mas o card do kanban (árvore separada em memória) fica
// preso na etapa antiga até um F5.
export const moverLeadDeEtapaNoKanban = ({ idLead, fromEtapa, toEtapa } = {}) => {
  if (!idLead || !fromEtapa || !toEtapa || fromEtapa === toEtapa) return
  store.dispatch('crm/moveLeadInKanban', { idLead, fromEtapa, toEtapa })
}

export const atualizarLeadOtimista = ({ leadData } = {}) => {
  if (!leadData || typeof leadData !== 'object') return
  const normalized = { ...leadData }
  if (!normalized.id && normalized.id_lead) normalized.id = normalized.id_lead

  store.dispatch('crm/addLeads', normalized)

  const idLead = normalized.id_lead || normalized.id
  if (idLead) {
    store.dispatch('crm/patchLeadInKanban', { idLead, patch: normalized })
  }
}

export const inserirNovoLeadOtimista = ({
  leadPayload,
  realLead = {},
  idFunil,
  idEtapa,
  baseCliente = {},
} = {}) => {
  if (!leadPayload || !idFunil || !idEtapa) return

  const idLead = realLead.id || realLead.id_lead
  const merged = {
    ...leadPayload,
    ...realLead,
    id: idLead,
    id_lead: idLead,
    etapa: { id: idEtapa, proximas_tarefas: [] },
    cliente: realLead.cliente || {
      nome_cliente: baseCliente.nome_cliente || leadPayload.nome,
      email: leadPayload.email || baseCliente.email,
      telefone: leadPayload.telefone || baseCliente.telefone,
      cpf: leadPayload.cpf || baseCliente.cpf,
    },
    nome: leadPayload.nome || baseCliente.nome_cliente,
    quantidade_comentarios: 0,
    tags: Array.isArray(leadPayload.tags) ? leadPayload.tags : [],
  }

  atualizarLeadOtimista({ leadData: merged })
  adicionarLeadAoKanban({ lead: merged, idFunil, idEtapa })
}

// Cada entrada mapeia uma chave de `clienteData` para o patch flat. `target`
// cobre o caso comum (mesmo valor, possivelmente renomeado); `apply` cobre
// campos com efeito colateral (id_origem também resolve o label `origem`).
const PATCH_FLAT_FIELDS = [
  { source: 'nome_cliente', target: 'nome' },
  { source: 'email', target: 'email' },
  { source: 'telefone', target: 'telefone' },
  { source: 'cpf', target: 'cpf' },
  { source: 'id_profissional_responsavel', target: 'id_profissional_responsavel' },
  // guard !== undefined: edição comum não apaga a foto; null (remoção) propaga
  { source: 'foto_url', target: 'foto_url' },
  {
    source: 'id_origem',
    apply: (patch, clienteData) => {
      patch.id_origem = clienteData.id_origem
      patch.origem = resolveOrigemLabel(clienteData.id_origem, clienteData.origem)
    },
  },
  // spec-007 — badge de sugestões de IA aguardando curadoria (global-modals.vue
  // patcha isso após accept/decline no ia-suggestions-modal, sem refetch do kanban).
  { source: 'possui_sugestao_ia', target: 'possui_sugestao_ia' },
  { source: 'qtd_sugestoes_ia', target: 'qtd_sugestoes_ia' },
]

// Monta o patch flat só com as chaves que `clienteData` de fato trouxe.
// Guard obrigatório: `clienteData` pode ser um patch parcial de 1 campo
// (ex: crm-edit-field-modal editando só `id_origem`) — sem o guard,
// `nome: clienteData.nome_cliente` cria a chave `nome` com valor
// `undefined` mesmo quando o nome não mudou, e o spread `{...lead,
// ...patchFlat}` apaga o nome/email/telefone/cpf já exibidos no lead.
const buildPatchFlat = (clienteData) => {
  const patch = {}
  PATCH_FLAT_FIELDS.forEach(({ source, target, apply }) => {
    if (clienteData[source] === undefined) return
    if (apply) {
      apply(patch, clienteData)
    } else {
      patch[target] = clienteData[source]
    }
  })
  return patch
}

export const atualizarClienteNosLeads = ({
  idClienteApp,
  clienteData,
} = {}) => {
  if (!idClienteApp || !clienteData) return
  const flatLeads = (store.state.crm.leads.data || []).filter(
    (l) => l && l.id_cliente_app === idClienteApp
  )
  if (!flatLeads.length) return

  const patchFlat = buildPatchFlat(clienteData)

  flatLeads.forEach((lead) => {
    const merged = {
      ...lead,
      ...patchFlat,
      cliente: { ...lead.cliente, ...clienteData },
    }
    atualizarLeadOtimista({ leadData: merged })
  })
}
